import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row, Select, Space, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../lib/api';
import AnalyticsFilters from '../components/AnalyticsFilters';
import type { AnalyticsEvent, AnalyticsSummaryItem } from '../types';

const { Title, Text } = Typography;

type DailyChartItem = {
  date: string;
  label: string;
  count: number;
};

function formatChartDate(date: string) {
  const parsed = new Date(`${date}T00:00:00`);
  return `${parsed.getMonth() + 1}/${parsed.getDate()}`;
}

function getLocalDateString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildLast30EventDays(events: AnalyticsEvent[]): DailyChartItem[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayKey = getLocalDateString(today);
  const counts = new Map<string, number>();
  for (let index = 29; index >= 0; index -= 1) {
    const date = new Date(today);
    date.setDate(date.getDate() - index);
    counts.set(getLocalDateString(date), 0);
  }
  for (const event of events) {
    const timestamp = event.occurredAt || event.createdAt;
    if (!timestamp) continue;
    const key = getLocalDateString(new Date(timestamp));
    if (counts.has(key)) {
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }
  return Array.from(counts.entries()).map(([date, count]) => ({
    date,
    count,
    label: date === todayKey ? 'Today' : formatChartDate(date),
  }));
}

function ColumnChart({ title, items }: { title: string; items: DailyChartItem[] }) {
  const max = Math.max(...items.map((item) => item.count), 1);
  return (
    <Card>
      <Title level={4}>{title}</Title>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 200 }}>
        {items.map((item) => {
          const height = Math.max((item.count / max) * 180, 2);
          return (
            <div key={item.date} style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <div
                title={`${item.date}: ${item.count}`}
                style={{
                  height,
                  background: 'linear-gradient(180deg, #f46524 0%, #f08f4b 100%)',
                  borderRadius: '4px 4px 0 0',
                  position: 'relative',
                }}
              >
                {item.count > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -18,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#f46524',
                    }}
                  >
                    {item.count}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: '#666',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.label}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function AnalyticsEventsPage() {
  const [apps, setApps] = useState<AnalyticsSummaryItem[]>([]);
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [selectedFunction, setSelectedFunction] = useState<string | null>(null);
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [chartEvents, setChartEvents] = useState<AnalyticsEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [ignoreAnnea, setIgnoreAnnea] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    loadApps();
  }, [ignoreAnnea, selectedOrgId, selectedUserId]);

  useEffect(() => {
    loadEvents();
    loadChartEvents();
  }, [selectedApp, selectedFunction, ignoreAnnea, selectedOrgId, selectedUserId]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = window.setInterval(() => {
      loadEvents();
      loadChartEvents();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, selectedApp, selectedFunction, ignoreAnnea, selectedOrgId, selectedUserId]);

  const loadApps = async () => {
    try {
      const scope = selectedUserId ? 'user' : selectedOrgId ? 'org' : 'global';
      const scopeId = selectedUserId || selectedOrgId || undefined;
      const data = await api.analytics.summary(scope, scopeId, { ignoreAnnea });
      const nextApps: AnalyticsSummaryItem[] = data.apps || [];
      setApps(nextApps);
      if (selectedApp && !nextApps.some((app) => app.appSlug === selectedApp)) {
        setSelectedApp(null);
        setSelectedFunction(null);
      }
    } catch (error) {
      console.error('[AnalyticsEvents] Failed to load apps', error);
      setApps([]);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await api.analytics.listEvents({
        appSlug: selectedApp || undefined,
        days: 90,
        limit: 100,
        functionName: selectedFunction || undefined,
        organizationId: selectedOrgId || undefined,
        userId: selectedUserId || undefined,
        ignoreAnnea,
      });
      setEvents(data.events || []);
      setLastUpdated(new Date().toLocaleString());
    } catch (error) {
      console.error('[AnalyticsEvents] Failed to load events', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const loadChartEvents = async () => {
    setChartLoading(true);
    try {
      const data = await api.analytics.listEvents({
        appSlug: selectedApp || undefined,
        days: 30,
        limit: 10000,
        functionName: selectedFunction || undefined,
        organizationId: selectedOrgId || undefined,
        userId: selectedUserId || undefined,
        ignoreAnnea,
      });
      setChartEvents(data.events || []);
    } catch (error) {
      console.error('[AnalyticsEvents] Failed to load chart events', error);
      setChartEvents([]);
    } finally {
      setChartLoading(false);
    }
  };

  const refreshEvents = async () => {
    await Promise.all([loadApps(), loadEvents(), loadChartEvents()]);
  };

  const functionOptions = useMemo(() => {
    const unique = new Set<string>();
    events.forEach((event) => {
      if (event.functionName) {
        unique.add(event.functionName);
      }
    });
    return Array.from(unique).sort();
  }, [events]);
  const dailyEvents = useMemo(() => buildLast30EventDays(chartEvents), [chartEvents]);

  const columns: ColumnsType<AnalyticsEvent> = [
    {
      title: 'Time',
      dataIndex: 'occurredAt',
      key: 'occurredAt',
      minWidth: 100,
      ellipsis: true,
      render: (value) => (
        <Text style={{ whiteSpace: 'wrap' }}>
          {new Date(value).toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'App',
      dataIndex: 'appSlug',
      key: 'appSlug',
      width: 200,
      ellipsis: true,
      render: (value, record) => (
        <div>
          <Text code style={{ whiteSpace: 'nowrap' }}>{value || '—'}</Text>
          {record.appName ? (
            <div>
              <Text type="secondary" ellipsis style={{ maxWidth: 210 }} title={record.appName}>
                {record.appName}
              </Text>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Function',
      dataIndex: 'functionName',
      key: 'functionName',
      width: 180,
      ellipsis: true,
      render: (value) => value || '—',
    },
    {
      title: 'Event',
      dataIndex: 'eventName',
      key: 'eventName',
      width: 170,
      ellipsis: true,
    },
    {
      title: 'Organization',
      dataIndex: 'organizationId',
      key: 'organizationId',
      width: 220,
      ellipsis: true,
      render: (value, record) => record.organizationName || value || '—',
    },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 220,
      ellipsis: true,
      render: (value, record) => record.userName || value || '—',
    },
    {
      title: 'Device',
      key: 'device',
      width: 160,
      ellipsis: true,
      render: (_, record) =>
        [record.os, record.browser].filter(Boolean).join(' · ') || '—',
    },
    {
      title: 'Context',
      key: 'context',
      width: 170,
      ellipsis: true,
      render: (_, record) => (
        <div>
          <div>{record.deviceType || '—'}</div>
          <Text type="secondary">
            {record.screenWidth && record.screenHeight
              ? `${record.screenWidth}x${record.screenHeight}`
              : ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Specific',
      dataIndex: 'specific',
      key: 'specific',
      width: 420,
      render: (value) => (
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {value ? JSON.stringify(value, null, 2) : '—'}
        </pre>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Usage Events</Title>
          <Text type="secondary">
            Detailed event stream{lastUpdated ? ` · Updated ${lastUpdated}` : ''}
          </Text>
        </div>
        <Space>
          <Text type="secondary">Auto refresh</Text>
          <Switch checked={autoRefresh} onChange={setAutoRefresh} />
          <Button onClick={() => void refreshEvents()} loading={loading || chartLoading}>
            Refresh
          </Button>
        </Space>
      </div>
      <AnalyticsFilters
        ignoreAnnea={ignoreAnnea}
        organizationId={selectedOrgId}
        userId={selectedUserId}
        onIgnoreAnneaChange={setIgnoreAnnea}
        onOrganizationChange={(value) => {
          setSelectedOrgId(value);
          setSelectedUserId(null);
        }}
        onUserChange={setSelectedUserId}
      />
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 240 }}>
            <Text type="secondary">App</Text>
            <Select
              style={{ width: '100%' }}
              value={selectedApp || undefined}
              onChange={(value) => {
                setSelectedFunction(null);
                setSelectedApp(value || null);
              }}
              allowClear
              placeholder="All apps"
              options={apps.map((app) => ({
                value: app.appSlug,
                label: app.appName,
              }))}
            />
          </div>
          <div style={{ minWidth: 240 }}>
            <Text type="secondary">Function</Text>
            <Select
              style={{ width: '100%' }}
              value={selectedFunction || undefined}
              onChange={(value) => setSelectedFunction(value || null)}
              allowClear
              placeholder="All functions"
              options={functionOptions.map((name) => ({ value: name, label: name }))}
            />
          </div>
        </div>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <ColumnChart
            title={chartLoading ? 'Events per Day (Last 30 Days) - Loading' : 'Events per Day (Last 30 Days)'}
            items={dailyEvents}
          />
        </Col>
      </Row>

      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={events}
          scroll={{ x: 1970 }}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No events found.' }}
        />
      </Card>
    </div>
  );
}
